/**
 * Spec 0045 — per-tenant GoHighLevel (GHL) integration management
 * (saas-auth SASATH-0053).
 *
 * Three SUPER_ADMIN-gated methods on saas-auth's existing tenant-scoped
 * `/api/saas-admin/tenants/{tenantId}/ghl-integration` surface: read (masked) /
 * upsert / delete. saas-admin stores nothing for GHL — it just calls saas-auth
 * and renders masked metadata.
 *
 * ENVELOPE: the shipped SASATH-0053 (#168) wraps every response in the standard
 * `ApiResponse<T>` envelope (verified against the shipped `SaasAdminController`),
 * so this module uses the strict `request()` + `unwrap()` pattern of
 * `members.ts` — NOT the tolerant parsing 0044 needed for an unconfirmed shape.
 *
 * SECRET HYGIENE: the GHL private integration token is write-only. It is never
 * returned by `get` (only `tokenSet`/`tokenSuffix`), never logged, and is only
 * ever sent on `set` when the admin actually types a new value — the caller
 * omits `privateIntegrationToken` from the request body otherwise.
 */
import { request, unwrap } from '../client';
import { ApiError } from '../errors';
import type { ApiResponse, GhlIntegrationDto, SetGhlIntegrationRequest } from '../types';

const base = (tenantId: number) => `/api/saas-admin/tenants/${tenantId}/ghl-integration`;

export const ghlIntegrationApi = {
  /**
   * Read the tenant's GHL integration (token masked). A 404 means the tenant has
   * no integration configured — that is a normal empty state, mapped to `null`
   * rather than surfaced as an error. Any other failure (incl. 403) propagates.
   */
  async get(tenantId: number): Promise<GhlIntegrationDto | null> {
    try {
      const res = await request<ApiResponse<GhlIntegrationDto>>('GET', base(tenantId));
      return unwrap(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  /**
   * Create or replace the tenant's GHL integration (upsert). The caller must
   * omit `privateIntegrationToken` from `req` when the admin left the password
   * field blank on an existing integration — the backend sets the token field
   * unconditionally, so resending an empty/absent token would clear it.
   */
  async set(tenantId: number, req: SetGhlIntegrationRequest): Promise<GhlIntegrationDto> {
    const res = await request<ApiResponse<GhlIntegrationDto>>('PUT', base(tenantId), {
      json: req,
    });
    return unwrap(res);
  },

  /** Delete the tenant's GHL integration. */
  async remove(tenantId: number): Promise<void> {
    await request<unknown>('DELETE', base(tenantId));
  },
};
